/**
 * 고급 음원 인식 및 분석
 *
 * 단계별 Fallback 전략:
 * 1. 파일 메타데이터 (ID3 태그) 추출
 * 2. 파일 이름 파싱
 * 3. 오디오 특징 기반 유사 트랙 검색
 * 4. Web Audio API Fallback
 */

import { extractMetadata } from "./music-metadata";
import { analyzeAudioFile } from "./web-audio-analysis";
import {
  searchSpotifyTrack,
  analyzeSpotifyTrack,
  getSpotifyTrackMetadata,
  getTrackRecommendations,
  type SpotifyRecommendationItem,
} from "./spotify-music-analysis";
import type { AudioAnalysisResult } from "./audio-analysis";
import type { SimilarArtistMatch } from "./music-similarity";

interface AudioCharacteristics {
  tempo: number;
  energy: number;
  duration: number;
  key: string;
}

interface SpotifyRecommendationTrack {
  id: string;
  name: string;
  popularity: number;
  artists: Array<{ name: string }>;
}

interface SpotifyRecommendationsApiResponse {
  tracks?: SpotifyRecommendationTrack[];
}

/**
 * 오디오 특징으로 Spotify에서 유사한 트랙 검색
 * 파일명/메타데이터가 없을 때 사용
 */
async function searchByAudioCharacteristics(
  characteristics: AudioCharacteristics
): Promise<string | null> {
  try {
    const accessToken = await getAccessToken();

    // Spotify Recommendations API 사용
    // 비슷한 템포, 에너지를 가진 트랙 검색
    const params = new URLSearchParams({
      limit: "10",
      seed_genres: "pop,rock,electronic", // 일반적인 장르
      target_tempo: characteristics.tempo.toString(),
      target_energy: characteristics.energy.toString(),
      target_duration_ms: (characteristics.duration * 1000).toString(),
    });

    const response = await fetch(
      `https://api.spotify.com/v1/recommendations?${params}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as SpotifyRecommendationsApiResponse;

    if (data.tracks && data.tracks.length > 0) {
      // 가장 인기 있는 트랙 반환
      const bestMatch = data.tracks.reduce<SpotifyRecommendationTrack>((best, current) =>
        current.popularity > best.popularity ? current : best
      , data.tracks[0]);

      console.log(
        "🎵 오디오 특징 기반 매칭:",
        bestMatch.name,
        "-",
        bestMatch.artists[0].name
      );
      return bestMatch.id;
    }

    return null;
  } catch (error) {
    console.error("❌ 오디오 특징 검색 실패:", error);
    return null;
  }
}

async function getAccessToken(): Promise<string> {
  // 클라이언트 사이드에서는 Spotify API를 직접 호출할 수 없음
  if (typeof window !== "undefined") {
    throw new Error("Spotify API는 서버 사이드에서만 사용 가능합니다");
  }

  const clientId = process.env.SPOTIFY_CLIENT_ID;
  const clientSecret = process.env.SPOTIFY_CLIENT_SECRET;

  if (!clientId || !clientSecret) {
    throw new Error("Spotify API credentials not found");
  }

  const response = await fetch("https://accounts.spotify.com/api/token", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: `Basic ${Buffer.from(`${clientId}:${clientSecret}`).toString("base64")}`,
    },
    body: "grant_type=client_credentials",
  });

  const data = await response.json();
  return data.access_token;
}

/**
 * 향상된 음악 인식 및 분석
 * 여러 방법을 시도하여 최상의 결과 제공
 */
export interface HybridSpotifyDetails {
  trackId: string;
  title: string;
  artist: string;
  album: string;
  albumArt: string;
  popularity: number;
  releaseDate: string;
  previewUrl?: string | null;
  explicit?: boolean;
  durationMs?: number;
  spotifyArtistId?: string;
  artistPopularity?: number;
  artistFollowers?: number;
  genres?: string[];
  spotifyRecommendations?: SpotifyRecommendationItem[];
  similarArtists?: SimilarArtistMatch[];
}

export interface HybridRecognitionResult extends AudioAnalysisResult {
  spotifyData?: HybridSpotifyDetails;
  analysisSource: "hybrid" | "web-audio";
}

export async function recognizeAndAnalyze(
  audioFile: File
): Promise<HybridRecognitionResult> {
  console.log("🎯 향상된 음악 인식 시작");

  // 1️⃣ 항상 Web Audio로 기본 분석
  const webAudioResult = await analyzeAudioFile(audioFile);
  console.log("✅ Web Audio 분석 완료:", {
    tempo: webAudioResult.tempo,
    key: webAudioResult.key,
    energy: webAudioResult.energyLevel,
  });

  try {
    // 2️⃣ 메타데이터 추출 시도
    const metadata = await extractMetadata(audioFile);
    console.log("📝 메타데이터:", metadata);

    let spotifyTrackId: string | null = null;

    // 3️⃣ 전략 A: 메타데이터로 검색
    if (metadata.artist || metadata.title) {
      console.log("🔍 전략 A: 메타데이터 검색");
      spotifyTrackId = await searchSpotifyTrack({
        artist: metadata.artist,
        track: metadata.title,
        album: metadata.album,
      });
    }

    // 4️⃣ 전략 B: 오디오 특징으로 유사 트랙 검색
    if (!spotifyTrackId) {
      console.log("🔍 전략 B: 오디오 특징 기반 검색");
      spotifyTrackId = await searchByAudioCharacteristics({
        tempo: webAudioResult.tempo,
        energy: webAudioResult.energyLevel,
        duration: webAudioResult.duration,
        key: webAudioResult.key,
      });
    }

    // 5️⃣ Spotify 데이터가 있으면 병합
    if (spotifyTrackId) {
      console.log("🎧 Spotify 트랙 발견, 정확한 데이터로 교체");

      const [spotifyAnalysis, spotifyMetadata] = await Promise.all([
        analyzeSpotifyTrack(spotifyTrackId),
        getSpotifyTrackMetadata(spotifyTrackId),
      ]);

      const spotifyRecommendations = await getTrackRecommendations(
        spotifyTrackId,
        spotifyAnalysis.audioFeatures
      );

      // Spotify 데이터를 Web Audio 구조에 병합
      const hybridResult: HybridRecognitionResult = {
        ...webAudioResult,
        // Spotify 데이터 우선 적용
        tempo: spotifyAnalysis.tempo,
        key: spotifyAnalysis.key,
        timeSignature: spotifyAnalysis.timeSignature,
        duration: spotifyAnalysis.duration,
        rhythmStability: spotifyAnalysis.rhythmStability,
        energyLevel: spotifyAnalysis.energyLevel,
        dynamicRange: spotifyAnalysis.dynamicRange,
        mood: spotifyAnalysis.mood,
        emotion: spotifyAnalysis.emotion,
        emotionScores: spotifyAnalysis.emotionScores,
        confidence: spotifyAnalysis.confidence,
        // Spotify 메타데이터 추가
        genre: spotifyMetadata?.genres[0] || webAudioResult.genre,
        spotifyData: spotifyMetadata
          ? {
              trackId: spotifyTrackId,
              title: spotifyMetadata.title,
              artist: spotifyMetadata.artist,
              album: spotifyMetadata.album,
              albumArt: spotifyMetadata.albumArt,
              popularity: spotifyMetadata.popularity,
              releaseDate: spotifyMetadata.releaseDate,
              previewUrl: spotifyMetadata.previewUrl,
              explicit: spotifyMetadata.explicit,
              durationMs: spotifyMetadata.durationMs,
              spotifyArtistId: spotifyMetadata.spotifyArtistId,
              artistPopularity: spotifyMetadata.artistPopularity,
              artistFollowers: spotifyMetadata.artistFollowers,
              genres: spotifyMetadata.genres,
              spotifyRecommendations,
            }
          : undefined,
        analysisSource: "hybrid" as const,
      };

      return hybridResult;
    }

    // 6️⃣ Spotify 실패 시 Web Audio 결과만 반환
    console.log("⚠️ Spotify 매칭 실패, Web Audio 결과 사용");
    return {
      ...webAudioResult,
      analysisSource: "web-audio" as const,
    };
  } catch (error) {
    console.error("❌ 인식 오류:", error);
    return {
      ...webAudioResult,
      analysisSource: "web-audio" as const,
    };
  }
}
